"use client";

import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Search, X, XCircle } from "lucide-react";
import { searchablePages } from "@/constants/searchablePages";
import Link from "next/link";
import { useRouter } from "next/navigation";

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);
  const router = useRouter();
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const isKeyboardNav = useRef(false);

  // Filter pages based on query
  const filteredPages = searchablePages.filter((page) => {
    if (!query.trim()) return true; // Show all by default
    const cleanQuery = query.toLowerCase().trim();
    const matchesTitle = page.title.toLowerCase().includes(cleanQuery);
    const matchesKeywords = page.keywords.some((kw) =>
      kw.toLowerCase().includes(cleanQuery)
    );
    const matchesPath = page.path.toLowerCase().includes(cleanQuery);
    return matchesTitle || matchesKeywords || matchesPath;
  });

  // Reset selected index when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      // Auto-focus input on open
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Handle outside click to close
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, onClose]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        isKeyboardNav.current = true;
        setSelectedIndex((prev) =>
          filteredPages.length > 0 ? (prev + 1) % filteredPages.length : 0
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        isKeyboardNav.current = true;
        setSelectedIndex((prev) =>
          filteredPages.length > 0
            ? (prev - 1 + filteredPages.length) % filteredPages.length
            : 0
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (filteredPages.length > 0 && selectedIndex >= 0 && selectedIndex < filteredPages.length) {
          const selectedPage = filteredPages[selectedIndex];
          router.push(selectedPage.path);
          onClose();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, filteredPages, selectedIndex, onClose, router]);

  // Scroll active item into view — only on keyboard navigation
  useEffect(() => {
    if (!isKeyboardNav.current) return;
    isKeyboardNav.current = false;
    if (listRef.current && selectedIndex >= 0) {
      const activeEl = listRef.current.children[selectedIndex] as HTMLElement;
      if (activeEl) {
        const container = listRef.current;
        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;
        const elemTop = activeEl.offsetTop;
        const elemBottom = elemTop + activeEl.clientHeight;

        if (elemTop < containerTop) {
          container.scrollTop = elemTop;
        } else if (elemBottom > containerBottom) {
          container.scrollTop = elemBottom - container.clientHeight;
        }
      }
    }
  }, [selectedIndex]);

  if (!isOpen || !mounted) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/60 backdrop-blur-sm px-4 pt-[12vh] transition-opacity duration-300">
      <div
        ref={modalRef}
        className="w-full max-w-2xl bg-white rounded-[24px] shadow-2xl overflow-hidden flex flex-col max-h-[75vh] animate-in fade-in-0 zoom-in-95 duration-200"
      >
        {/* Search Input Bar */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-[#E2E8F0]">
          <Search size={22} className="text-[#94A3B8] shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages..."
            className="w-full bg-transparent text-[#1E293B] text-[17px] font-onest placeholder:text-[#94A3B8] border-none outline-none focus:ring-0 focus:outline-none"
          />
          {query ? (
            <button
              onClick={() => {
                setQuery("");
                inputRef.current?.focus();
              }}
              className="text-[#94A3B8] hover:text-[#475569] transition-colors shrink-0"
              aria-label="Clear search"
            >
              <XCircle size={20} />
            </button>
          ) : (
            <button
              onClick={onClose}
              className="text-[#94A3B8] hover:text-[#475569] transition-colors shrink-0"
              aria-label="Close search"
            >
              <X size={20} />
            </button>
          )}
        </div>

        {/* Results List */}
        <div
          ref={listRef}
          className="overflow-y-auto p-3 flex flex-col gap-1 max-h-[420px]"
        >
          {filteredPages.length > 0 ? (
            filteredPages.map((page, index) => {
              const isSelected = index === selectedIndex;
              return (
                <Link
                  key={page.path}
                  href={page.path}
                  onClick={onClose}
                  className={`flex flex-col gap-0.5 px-6 py-3.5 rounded-[16px] transition-all text-left w-full ${
                    isSelected
                      ? "bg-[#EDEDED] text-black"
                      : "hover:bg-[#EDEDED]/50 text-slate-700"
                  }`}
                  onMouseEnter={() => { isKeyboardNav.current = false; setSelectedIndex(index); }}
                >
                  <span className="font-onest text-[16px] font-bold text-[#1A1A1A]">
                    {page.title}
                  </span>
                  <span className="font-onest text-[13px] text-slate-400 font-normal">
                    {page.path}
                  </span>
                </Link>
              );
            })
          ) : (
            <div className="py-12 text-center">
              <p className="font-onest text-[15px] text-slate-400 font-medium">
                No results found for &ldquo;{query}&rdquo;
              </p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SearchModal;
